import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Input, Button, Select, Upload, Tag, message, Modal } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  UploadOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";
import useIsSafetyAdmin from "./shared/useIsSafetyAdmin";
import useInvestigationAccess from "./shared/useInvestigationAccess";
import useInvestigationHandlers from "./shared/useInvestigationHandlers";
import InvestigationStepper from "./shared/InvestigationStepper";
import useCorrectiveActionContext, {
  todayISO,
  validateCorrectiveActions,
  validateCorrectiveActionDescriptions,
} from "./shared/correctiveActionHelpers";
import LegacyFactualReadOnly from "./LegacyFactualReadOnly";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";

const { TextArea } = Input;
const { Option } = Select;

// Spec section keys — match the section_key column on attachments so the API
// can group uploads per spec section. New keys must also be reflected in the
// backend if any server-side grouping is added.
const SECTION_KEYS = {
  SOP_PERMIT: "sop_permit",
  EVIDENCE_PHOTO: "evidence_photo",
  EVIDENCE_VIDEO: "evidence_video",
  EVIDENCE_PHYSICAL: "evidence_physical",
  EVIDENCE_DIGITAL: "evidence_digital",
  WITNESS_STATEMENT: "witness_statement",
};

const STEPS = [
  { number: 1, title: "Incident Metadata", description: "Auto-populated, locked" },
  { number: 2, title: "Investigation Control", description: "Investigator & dates" },
  { number: 3, title: "Chronology & Equipment", description: "Facts only" },
  { number: 4, title: "Environment & Evidence", description: "Conditions & files" },
  { number: 5, title: "Witnesses & Actions", description: "Statements & follow-ups" },
];

const WEATHER_OPTIONS = ["Fine", "Rain", "Wind", "Other"];
const LIGHTING_OPTIONS = ["Daylight", "Artificial Lighting", "Low Light"];
const GROUND_OPTIONS = ["Dry", "Wet", "Uneven", "Other"];
const TASK_TYPE_OPTIONS = ["Normal", "Non-routine"];
const DIGITAL_RECORD_OPTIONS = ["Fieldin", "Telemetry", "Other"];
const YES_NO = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const DEFAULT_FACTUAL_V2 = {
  incidentMetadata: {
    dateTime: "",
    type: "",
    activity: "",
    description: "",
    locationBusinessArea: "",
    latitude: null,
    longitude: null,
  },
  peopleInvolvement: {
    contractorVisitorInvolved: null,
    personnelNotified: "",
  },
  impactOutcome: {
    damageDetails: "",
    environmentImpactDetails: "",
    outcome: "",
  },
  immediateResponse: {
    immediateActions: "",
    sceneSecured: "",
  },
  controlsInPlace: {
    sopPolicyDetails: "",
  },
  chronology: { before: "", during: "", after: "" },
  equipment: { equipmentPlantInvolved: "", taskBeingPerformed: "", taskType: "" },
  environmental: {
    weather: "",
    lighting: "",
    groundSurface: [],
    noiseVisibility: "",
  },
  evidence: {
    photographsCollected: null,
    videoCctvAvailable: null,
    physicalEvidenceCollected: null,
    physicalEvidenceStorageLocation: "",
    digitalRecordsRetrieved: [],
    digitalRecordsOther: "",
  },
  witnesses: { identified: null, list: [] },
  correctiveActions: [],
};

const defaultWitness = () => ({ name: "", company: "", statement: "" });

const defaultCorrectiveAction = () => ({
  id: undefined,
  action: "",
  assignedTo: undefined,
  dueDate: "",
  priority: "Medium",
});

const deepMerge = (defaults, incoming) => {
  if (!incoming || typeof incoming !== "object") return defaults;
  const out = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (
      defaults[key] &&
      typeof defaults[key] === "object" &&
      !Array.isArray(defaults[key]) &&
      incoming[key] &&
      typeof incoming[key] === "object" &&
      !Array.isArray(incoming[key])
    ) {
      out[key] = { ...defaults[key], ...incoming[key] };
    } else if (incoming[key] !== undefined) {
      out[key] = incoming[key];
    }
  }
  return out;
};

const parseContent = (content) => {
  if (!content) return DEFAULT_FACTUAL_V2;
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object") {
      const merged = deepMerge(DEFAULT_FACTUAL_V2, parsed);
      merged.correctiveActions = Array.isArray(parsed.correctiveActions)
        ? parsed.correctiveActions.map((ca) => ({
            ...defaultCorrectiveAction(),
            ...ca,
          }))
        : [];
      merged.witnesses = {
        identified: parsed.witnesses?.identified ?? null,
        list: Array.isArray(parsed.witnesses?.list)
          ? parsed.witnesses.list.map((w) => ({ ...defaultWitness(), ...w }))
          : [],
      };
      // Preserve the legacy-content bundle that the m260608 backfill writes
      // for in-progress investigations migrated from the pre-Brett shape.
      // Kept verbatim so subsequent saves round-trip it; the form surfaces
      // it via a read-only reference banner at the top of Step 1.
      if (parsed.__legacy && typeof parsed.__legacy === "object") {
        merged.__legacy = parsed.__legacy;
      }
      return merged;
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_FACTUAL_V2;
};

const parseSnapshot = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Group section uploads by section_key. A second pass appends untagged
// investigation-scoped attachments under a "general" bucket so nothing is lost.
const groupAttachmentsBySection = (attachments) => {
  const groups = {};
  (attachments || []).forEach((att) => {
    const key = att.section_key || "general";
    if (!groups[key]) groups[key] = [];
    groups[key].push(att);
  });
  return groups;
};

const fileListFromAttachments = (atts) =>
  (atts || []).map((att) => ({
    uid: String(att.id),
    name: att.display_name || (att.url ? att.url.split("/").pop() : `Attachment #${att.id}`),
    status: "done",
    url: att.full_path || att.url,
  }));

const FactualInvestigation = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const isSafetyAdmin = useIsSafetyAdmin();

  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FACTUAL_V2);
  const [reviewComment, setReviewComment] = useState("");
  const [commencementDate, setCommencementDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [sectionFileLists, setSectionFileLists] = useState({});
  const [sectionAttachmentIds, setSectionAttachmentIds] = useState({});
  const [pendingSectionUpdates, setPendingSectionUpdates] = useState([]);
  // Local loading state — the shared hook owns its own saving/submitting
  // booleans but those setters are private to the hook. Our custom Factual
  // v2 handlers below need a parallel flag so the buttons can show a
  // spinner during save/submit.
  const [localSaving, setLocalSaving] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);

  const snapshot = useMemo(
    () => parseSnapshot(investigation?.incident_snapshot),
    [investigation?.incident_snapshot]
  );
  const isLegacy = investigation?.form_version === 1 || investigation?.is_legacy;
  const metadataLocked = investigation?.metadata_locked !== false;

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentData, investigationData] = await Promise.all([
        incidentService.getById(incidentId),
        incidentService.getInvestigation(incidentId),
      ]);
      setIncident(incidentData);
      setInvestigation(investigationData);
      if (investigationData) {
        setFormData(parseContent(investigationData.content));
        setReviewComment(investigationData.review_comment || "");
        setCommencementDate(investigationData.commencement_date || "");
        setCompletionDate(investigationData.completion_date || "");

        // Seed per-section upload UI state from existing attachments.
        const grouped = groupAttachmentsBySection(investigationData.evidence_attachments);
        const fileLists = {};
        const ids = {};
        Object.entries(grouped).forEach(([key, atts]) => {
          fileLists[key] = fileListFromAttachments(atts);
          ids[key] = atts.map((a) => a.id);
        });
        setSectionFileLists(fileLists);
        setSectionAttachmentIds(ids);
      }
    } catch (err) {
      console.error("Failed to load Factual investigation:", err);
      message.error("Failed to load investigation data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (incidentId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  const { canEdit, canSubmit, isViewOnly, isApproved, isSubmitted } =
    useInvestigationAccess(investigation, isSafetyAdmin);

  // Most metadata is sourced from the snapshot, but everything outside the
  // Incident Metadata slot stays editable for the investigator (per Q4).
  const canEditMetadata = canEdit && !metadataLocked;

  const { staff, isCALocked } = useCorrectiveActionContext(
    incidentId,
    incident?.property_id
  );

  const buildPayload = () => {
    // Bundle section_key tagging alongside the content + dates so the backend
    // can persist new uploads against their spec section in one round-trip.
    const evidenceAttachmentIds = Object.values(sectionAttachmentIds).flat();
    return {
      content: JSON.stringify(formData),
      evidence: investigation?.evidence || "",
      evidence_attachment_ids: evidenceAttachmentIds,
      section_attachments: pendingSectionUpdates,
      commencement_date: commencementDate || null,
      completion_date: completionDate || null,
    };
  };

  const {
    saving,
    submitting,
    approving,
    handleSaveDraft: rawSaveDraft,
    handleSubmit: rawSubmit,
    handleApprove,
    handleRequestMoreInfo,
  } = useInvestigationHandlers(incidentId, formData, investigation, reviewComment);

  // The shared handlers serialise formData → content. For Factual v2 we need
  // to forward the structured payload (dates + section_attachments), so we
  // wrap them with a thin call that runs editInvestigation directly. We use
  // the local saving/submitting flags here because the shared hook's flags
  // are internal to its own bypassed handlers.
  const handleSaveDraft = async () => {
    try {
      setLocalSaving(true);
      await incidentService.editInvestigation(incidentId, buildPayload());
      setPendingSectionUpdates([]);
      message.success("Investigation saved as draft.");
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to save draft.");
    } finally {
      setLocalSaving(false);
    }
  };

  const handleSubmit = () => {
    const error = validateCorrectiveActionDescriptions(formData.correctiveActions);
    if (error) {
      message.error(error);
      setCurrentStep(5);
      return;
    }
    Modal.confirm({
      title: "Submit Factual Investigation",
      content:
        "Submit this Factual Investigation for Safety Admin review? You can still edit it if more information is requested.",
      okButtonProps: { style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" } },
      onOk: async () => {
        try {
          setLocalSubmitting(true);
          // editInvestigation first so dates / section_attachments persist
          // before the status flip in submitInvestigation.
          await incidentService.editInvestigation(incidentId, buildPayload());
          setPendingSectionUpdates([]);
          await incidentService.submitInvestigation(incidentId, {
            content: JSON.stringify(formData),
            evidence: investigation?.evidence || "",
            evidence_attachment_ids: Object.values(sectionAttachmentIds).flat(),
          });
          message.success("Investigation submitted for review.");
          history.push("/incidents");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to submit investigation."
          );
        } finally {
          setLocalSubmitting(false);
        }
      },
    });
  };

  const onApproveClick = () => {
    const error = validateCorrectiveActions(formData.correctiveActions);
    if (error) {
      setCurrentStep(5);
      message.error(error);
      return;
    }
    handleApprove(formData.correctiveActions);
  };

  // Section-level helpers
  const updateNested = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const toggleGroundSurface = (option) => {
    setFormData((prev) => {
      const set = new Set(prev.environmental.groundSurface || []);
      if (set.has(option)) set.delete(option);
      else set.add(option);
      return {
        ...prev,
        environmental: { ...prev.environmental, groundSurface: Array.from(set) },
      };
    });
  };

  const toggleDigitalRecord = (option) => {
    setFormData((prev) => {
      const set = new Set(prev.evidence.digitalRecordsRetrieved || []);
      if (set.has(option)) set.delete(option);
      else set.add(option);
      return {
        ...prev,
        evidence: { ...prev.evidence, digitalRecordsRetrieved: Array.from(set) },
      };
    });
  };

  // Witnesses
  const addWitness = () => {
    setFormData((prev) => ({
      ...prev,
      witnesses: {
        ...prev.witnesses,
        list: [...(prev.witnesses.list || []), defaultWitness()],
      },
    }));
  };
  const removeWitness = (idx) => {
    setFormData((prev) => ({
      ...prev,
      witnesses: {
        ...prev.witnesses,
        list: prev.witnesses.list.filter((_, i) => i !== idx),
      },
    }));
  };
  const updateWitness = (idx, field, value) => {
    setFormData((prev) => {
      const list = [...prev.witnesses.list];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, witnesses: { ...prev.witnesses, list } };
    });
  };

  // Corrective actions
  const addCorrectiveAction = () => {
    setFormData((prev) => ({
      ...prev,
      correctiveActions: [...prev.correctiveActions, defaultCorrectiveAction()],
    }));
  };
  const removeCorrectiveAction = (idx) => {
    const target = formData.correctiveActions[idx];
    if (isCALocked(target)) {
      message.warning("Cannot remove a corrective action that has already started.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      correctiveActions: prev.correctiveActions.filter((_, i) => i !== idx),
    }));
  };
  const updateCorrectiveAction = (idx, field, value) => {
    setFormData((prev) => {
      const next = [...prev.correctiveActions];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, correctiveActions: next };
    });
  };

  // Per-section upload handler. Tracks per-section file list, ID list, and a
  // pending-updates queue so the server can stamp section_key on the next save.
  const buildUploadProps = (sectionKey, label) => ({
    action: apiEndpoint.attachments,
    headers: authHeader(true),
    name: "Attachment[file]",
    multiple: true,
    listType: "text",
    fileList: sectionFileLists[sectionKey] || [],
    data: { type: "investigation", storage: "filesystem", section_key: sectionKey },
    disabled: !canEdit,
    onChange: (info) => {
      if (
        info.file &&
        info.file.status === "done" &&
        info.file.response?.attachment
      ) {
        const newId = info.file.response.attachment.id;
        setSectionAttachmentIds((prev) => ({
          ...prev,
          [sectionKey]: [...(prev[sectionKey] || []), newId],
        }));
        setPendingSectionUpdates((prev) => [
          ...prev,
          { id: newId, section_key: sectionKey },
        ]);
      }
      setSectionFileLists((prev) => ({ ...prev, [sectionKey]: [...info.fileList] }));
    },
    onRemove: (file) => {
      const id = parseInt(file.uid, 10);
      setSectionAttachmentIds((prev) => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).filter((aid) => aid !== id),
      }));
    },
    children: (
      <Button icon={<UploadOutlined />} disabled={!canEdit}>
        {label || "Upload"}
      </Button>
    ),
  });

  const renderUpload = (sectionKey, label) => {
    const props = buildUploadProps(sectionKey, label);
    const { children, ...rest } = props;
    return <Upload {...rest}>{children}</Upload>;
  };

  const handleToggleMetadataLock = async () => {
    try {
      const updated = await incidentService.unlockInvestigationMetadata(
        incidentId,
        !metadataLocked
      );
      if (updated) setInvestigation(updated);
      message.success(
        metadataLocked
          ? "Incident metadata unlocked for editing."
          : "Incident metadata re-locked."
      );
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update metadata lock.");
    }
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="loading-container">
              <Spin size="large" />
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="not-found-container">
              <h2 className="not-found-title">Incident not found</h2>
              <span
                className="not-found-link"
                onClick={() => history.push("/incidents")}
              >
                Return to incidents
              </span>
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  // Q1 — legacy form_version=1 investigations are frozen read-only. Brett's
  // sign-off is pending but James has lean'd on this default; behaviour is
  // also reversible with a flag flip later if the call changes.
  if (isLegacy) {
    return (
      <LegacyFactualReadOnly
        incident={incident}
        investigation={investigation}
        onBack={() => history.push("/incidents")}
      />
    );
  }

  // === Snapshot-driven metadata cells ===
  const metaCellValue = (snapshotKey, contentSection, contentKey) => {
    // When metadata is locked, render the snapshot value (frozen). When
    // unlocked, render the editable copy in content so investigator edits
    // stay in the Factual (Q4 — no writeback to Incident Report).
    if (metadataLocked) {
      return snapshot ? snapshot[snapshotKey] : "";
    }
    return formData[contentSection]?.[contentKey];
  };

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          {/* Header */}
          <div className="inv-header">
            <div>
              <button
                className="inv-back-button"
                onClick={() => history.push("/incidents")}
              >
                <ArrowLeftOutlined /> Back to Incidents
              </button>
              <h1 className="inv-title">Factual Investigation</h1>
              <p className="inv-subtitle">
                {incident.report_number} — {incident.description || ""}
              </p>
            </div>
            <div className="inv-header-actions">
              {canEdit && (
                <Button
                  className="btn-save-draft"
                  onClick={handleSaveDraft}
                  loading={saving || localSaving}
                >
                  <SaveOutlined /> Save Draft
                </Button>
              )}
              {canSubmit && (
                <Button
                  type="primary"
                  className="btn-submit"
                  onClick={handleSubmit}
                  loading={submitting || localSubmitting}
                >
                  <SendOutlined /> Submit Investigation
                </Button>
              )}
            </div>
          </div>

          {isApproved && (
            <div className="readonly-notice">
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              This investigation has been approved and is read-only.
            </div>
          )}

          {isViewOnly && !isApproved && (
            <div
              className="readonly-notice"
              style={{
                background: "#eff6ff",
                borderColor: "#bfdbfe",
                color: "#1e40af",
              }}
            >
              You are viewing this investigation in read-only mode. Only the
              assigned investigator and Safety Admins can make changes.
            </div>
          )}

          {/* Safety Admin Review */}
          {isSafetyAdmin && isSubmitted && (
            <div className="review-section">
              <h3 className="review-title">Safety Admin Review</h3>
              <p className="review-status">
                This investigation has been submitted and is awaiting your review.
              </p>
              <TextArea
                rows={3}
                placeholder="Add a review comment (optional)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <div className="review-actions">
                <Button
                  className="btn-approve"
                  type="primary"
                  onClick={onApproveClick}
                  loading={approving}
                >
                  Approve Investigation
                </Button>
                <Button
                  className="btn-request-info"
                  onClick={handleRequestMoreInfo}
                  loading={approving}
                >
                  Request More Info
                </Button>
              </div>
            </div>
          )}

          <InvestigationStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={setCurrentStep}
          />

          <div className="form-card">
            {/* === STEP 1 — Incident Metadata, People, Impact, Immediate Response, Controls === */}
            {currentStep === 1 && (
              <div>
                {/* Brett 2026-06-10: in-progress investigations migrated
                    from the pre-v2 form get their old free-text preserved
                    under content.__legacy by the m260608 backfill. We
                    surface it here as a read-only reference so the
                    investigator can copy/paste it into the appropriate v2
                    slots (e.g. keyFindings → chronology) without losing
                    the work captured under the legacy form. */}
                {formData.__legacy && (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: 8,
                      padding: "12px 16px",
                      marginBottom: 16,
                      color: "#92400e",
                    }}
                  >
                    <strong>Migrated from legacy form</strong>
                    <p style={{ marginTop: 8, marginBottom: 8, fontSize: 13 }}>
                      This investigation was captured under the previous
                      Factual Investigation form. The text below is preserved
                      for your reference — copy any still-relevant content
                      into the v2 sections, then carry on.
                    </p>
                    {formData.__legacy.incidentSummary && (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 12, marginTop: 8 }}>
                          Incident Summary (legacy)
                        </div>
                        <div
                          style={{
                            background: "#fffdf6",
                            padding: 10,
                            borderRadius: 6,
                            whiteSpace: "pre-wrap",
                            fontSize: 13,
                          }}
                        >
                          {formData.__legacy.incidentSummary}
                        </div>
                      </>
                    )}
                    {formData.__legacy.keyFindings && (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 12, marginTop: 8 }}>
                          Key Findings (legacy)
                        </div>
                        <div
                          style={{
                            background: "#fffdf6",
                            padding: 10,
                            borderRadius: 6,
                            whiteSpace: "pre-wrap",
                            fontSize: 13,
                          }}
                        >
                          {formData.__legacy.keyFindings}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h2 className="step-title" style={{ marginBottom: 0 }}>
                    Step 1: Incident Metadata
                  </h2>
                  {isSafetyAdmin && canEdit && (
                    <Button
                      icon={metadataLocked ? <UnlockOutlined /> : <LockOutlined />}
                      onClick={handleToggleMetadataLock}
                    >
                      {metadataLocked ? "Unlock metadata" : "Re-lock metadata"}
                    </Button>
                  )}
                </div>
                <p className="step-description">
                  These fields were auto-populated from the Incident Report at the
                  time the Factual Investigation was created (snapshot). They are
                  read-only by default — Safety Admin can unlock if a correction
                  is required, in which case the change applies only to this
                  Factual record, not the original Incident Report.
                </p>

                <label className="field-label-sm">Date and Time of Incident</label>
                <Input
                  value={metaCellValue("date_time", "incidentMetadata", "dateTime") || ""}
                  onChange={(e) =>
                    updateNested("incidentMetadata", "dateTime", e.target.value)
                  }
                  disabled={!canEditMetadata}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Type of Incident</label>
                <Input
                  value={metaCellValue("type", "incidentMetadata", "type") || ""}
                  onChange={(e) =>
                    updateNested("incidentMetadata", "type", e.target.value)
                  }
                  disabled={!canEditMetadata}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Type of Activity</label>
                <Input
                  value={metaCellValue("activity", "incidentMetadata", "activity") || ""}
                  onChange={(e) =>
                    updateNested("incidentMetadata", "activity", e.target.value)
                  }
                  disabled={!canEditMetadata}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Description of Incident / Event</label>
                <TextArea
                  rows={4}
                  value={metaCellValue("description", "incidentMetadata", "description") || ""}
                  onChange={(e) =>
                    updateNested("incidentMetadata", "description", e.target.value)
                  }
                  disabled={!canEditMetadata}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Business Unit / Site</label>
                <Input
                  value={
                    metaCellValue(
                      "location_business_area",
                      "incidentMetadata",
                      "locationBusinessArea"
                    ) || ""
                  }
                  onChange={(e) =>
                    updateNested(
                      "incidentMetadata",
                      "locationBusinessArea",
                      e.target.value
                    )
                  }
                  disabled={!canEditMetadata}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Exact Location (lat / lng)</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
                  <Input
                    placeholder="Latitude"
                    value={metaCellValue("latitude", "incidentMetadata", "latitude") ?? ""}
                    disabled={!canEditMetadata}
                    onChange={(e) =>
                      updateNested("incidentMetadata", "latitude", e.target.value)
                    }
                  />
                  <Input
                    placeholder="Longitude"
                    value={metaCellValue("longitude", "incidentMetadata", "longitude") ?? ""}
                    disabled={!canEditMetadata}
                    onChange={(e) =>
                      updateNested("incidentMetadata", "longitude", e.target.value)
                    }
                  />
                </div>

                <h3 style={sectionHeading}>People and Involvement</h3>
                <label className="field-label-sm">
                  Contractor or Visitor Involved or Witnessed Incident
                </label>
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={formData.peopleInvolvement.contractorVisitorInvolved}
                  onChange={(v) =>
                    updateNested("peopleInvolvement", "contractorVisitorInvolved", v)
                  }
                  disabled={!canEdit}
                  placeholder="Select Yes or No"
                >
                  {YES_NO.map((opt) => (
                    <Option key={String(opt.value)} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>

                <label className="field-label-sm">Personnel Reported To</label>
                <Input
                  value={formData.peopleInvolvement.personnelNotified || ""}
                  onChange={(e) =>
                    updateNested("peopleInvolvement", "personnelNotified", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 24 }}
                />

                <h3 style={sectionHeading}>Impact and Outcome</h3>
                <label className="field-label-sm">Details of Any Damage</label>
                <TextArea
                  rows={3}
                  value={formData.impactOutcome.damageDetails || ""}
                  onChange={(e) => updateNested("impactOutcome", "damageDetails", e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label-sm">Details of Any Impact to Environment</label>
                <TextArea
                  rows={3}
                  value={formData.impactOutcome.environmentImpactDetails || ""}
                  onChange={(e) =>
                    updateNested("impactOutcome", "environmentImpactDetails", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label-sm">Outcome</label>
                <Input
                  value={formData.impactOutcome.outcome || ""}
                  onChange={(e) => updateNested("impactOutcome", "outcome", e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 24 }}
                />

                <h3 style={sectionHeading}>Immediate Response</h3>
                <label className="field-label-sm">
                  Immediate Actions Taken Following the Event
                </label>
                <TextArea
                  rows={3}
                  value={formData.immediateResponse.immediateActions || ""}
                  onChange={(e) =>
                    updateNested("immediateResponse", "immediateActions", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label-sm">How the Scene Was Secured</label>
                <TextArea
                  rows={3}
                  value={formData.immediateResponse.sceneSecured || ""}
                  onChange={(e) =>
                    updateNested("immediateResponse", "sceneSecured", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 24 }}
                />

                <h3 style={sectionHeading}>Controls in Place</h3>
                <label className="field-label-sm">
                  SOP or Permit to Work / JSA in Place for the Task
                </label>
                <TextArea
                  rows={3}
                  value={formData.controlsInPlace.sopPolicyDetails || ""}
                  onChange={(e) =>
                    updateNested("controlsInPlace", "sopPolicyDetails", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 12 }}
                />
                <p className="step-description" style={{ marginTop: 0 }}>
                  Upload supporting SOP / Permit / JSA documentation if available.
                </p>
                {renderUpload(SECTION_KEYS.SOP_PERMIT, "Upload SOP / Permit / JSA")}

                <div className="step-nav" style={{ justifyContent: "flex-end", marginTop: 32 }}>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(2)}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* === STEP 2 — Investigation Control === */}
            {currentStep === 2 && (
              <div>
                <h2 className="step-title">Step 2: Investigation Control</h2>
                <p className="step-description">
                  Record who is conducting the investigation and the timeframe.
                </p>

                <label className="field-label-sm">Investigation Type</label>
                <Input value="Factual Investigation" disabled style={{ marginBottom: 16 }} />

                <label className="field-label-sm">Investigator</label>
                <Input
                  value={investigation?.investigator_name || ""}
                  disabled
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Investigation Commencement Date</label>
                <Input
                  type="date"
                  value={commencementDate || ""}
                  onChange={(e) => setCommencementDate(e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">Factual Investigation Completion Date</label>
                <Input
                  type="date"
                  value={completionDate || ""}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />

                <div className="step-nav" style={{ marginTop: 32 }}>
                  <Button onClick={() => setCurrentStep(1)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(3)}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* === STEP 3 — Chronology + Equipment === */}
            {currentStep === 3 && (
              <div>
                <h2 className="step-title">Step 3: Chronology of Events & Equipment</h2>
                <div className="tips-box tips-box-blue" style={{ marginBottom: 20 }}>
                  Record observable facts only. Do not include assumptions, causes,
                  reasons, or conclusions.
                </div>

                <label className="field-label-sm">
                  What was occurring immediately before the incident
                </label>
                <TextArea
                  rows={4}
                  value={formData.chronology.before}
                  onChange={(e) => updateNested("chronology", "before", e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">
                  What occurred at the time of the incident
                </label>
                <TextArea
                  rows={4}
                  value={formData.chronology.during}
                  onChange={(e) => updateNested("chronology", "during", e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />

                <label className="field-label-sm">
                  What occurred immediately after the incident
                </label>
                <TextArea
                  rows={4}
                  value={formData.chronology.after}
                  onChange={(e) => updateNested("chronology", "after", e.target.value)}
                  disabled={!canEdit}
                  style={{ marginBottom: 24 }}
                />

                <h3 style={sectionHeading}>Equipment, Plant or Process</h3>
                <label className="field-label-sm">Equipment or Plant Involved</label>
                <Input
                  value={formData.equipment.equipmentPlantInvolved || ""}
                  onChange={(e) =>
                    updateNested("equipment", "equipmentPlantInvolved", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label-sm">Task Being Performed</label>
                <Input
                  value={formData.equipment.taskBeingPerformed || ""}
                  onChange={(e) =>
                    updateNested("equipment", "taskBeingPerformed", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label-sm">Task Type</label>
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={formData.equipment.taskType || undefined}
                  onChange={(v) => updateNested("equipment", "taskType", v)}
                  placeholder="Select task type"
                  disabled={!canEdit}
                >
                  {TASK_TYPE_OPTIONS.map((t) => (
                    <Option key={t} value={t}>
                      {t}
                    </Option>
                  ))}
                </Select>

                <div className="step-nav" style={{ marginTop: 32 }}>
                  <Button onClick={() => setCurrentStep(2)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(4)}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* === STEP 4 — Environmental + Evidence === */}
            {currentStep === 4 && (
              <div>
                <h2 className="step-title">Step 4: Environmental Conditions & Evidence</h2>

                <h3 style={sectionHeading}>Environmental and Site Conditions</h3>
                <label className="field-label-sm">Weather Conditions</label>
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={formData.environmental.weather || undefined}
                  onChange={(v) => updateNested("environmental", "weather", v)}
                  placeholder="Select weather"
                  disabled={!canEdit}
                >
                  {WEATHER_OPTIONS.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>

                <label className="field-label-sm">Lighting Conditions</label>
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={formData.environmental.lighting || undefined}
                  onChange={(v) => updateNested("environmental", "lighting", v)}
                  placeholder="Select lighting"
                  disabled={!canEdit}
                >
                  {LIGHTING_OPTIONS.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>

                <label className="field-label-sm">Ground or Surface Conditions</label>
                <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {GROUND_OPTIONS.map((opt) => {
                    const selected = (formData.environmental.groundSurface || []).includes(opt);
                    return (
                      <Tag.CheckableTag
                        key={opt}
                        checked={selected}
                        onChange={() => canEdit && toggleGroundSurface(opt)}
                        style={{
                          padding: "4px 12px",
                          fontSize: 14,
                          cursor: canEdit ? "pointer" : "default",
                        }}
                      >
                        {opt}
                      </Tag.CheckableTag>
                    );
                  })}
                </div>

                <label className="field-label-sm">
                  Noise or Visibility Issues Observed
                </label>
                <TextArea
                  rows={3}
                  placeholder="e.g. equipment noise, dust"
                  value={formData.environmental.noiseVisibility || ""}
                  onChange={(e) =>
                    updateNested("environmental", "noiseVisibility", e.target.value)
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: 24 }}
                />

                <h3 style={sectionHeading}>Evidence Collection</h3>

                <label className="field-label-sm">Photographs Collected</label>
                <Select
                  style={{ width: "100%", marginBottom: 12 }}
                  value={formData.evidence.photographsCollected}
                  onChange={(v) => updateNested("evidence", "photographsCollected", v)}
                  disabled={!canEdit}
                  placeholder="Yes / No"
                >
                  {YES_NO.map((o) => (
                    <Option key={String(o.value)} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
                {formData.evidence.photographsCollected === true &&
                  renderUpload(SECTION_KEYS.EVIDENCE_PHOTO, "Upload Photographs")}

                <label className="field-label-sm" style={{ marginTop: 16 }}>
                  Video or CCTV Available
                </label>
                <Select
                  style={{ width: "100%", marginBottom: 12 }}
                  value={formData.evidence.videoCctvAvailable}
                  onChange={(v) => updateNested("evidence", "videoCctvAvailable", v)}
                  disabled={!canEdit}
                  placeholder="Yes / No"
                >
                  {YES_NO.map((o) => (
                    <Option key={String(o.value)} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
                {formData.evidence.videoCctvAvailable === true &&
                  renderUpload(SECTION_KEYS.EVIDENCE_VIDEO, "Upload Video / CCTV")}

                <label className="field-label-sm" style={{ marginTop: 16 }}>
                  Physical Evidence Collected
                </label>
                <Select
                  style={{ width: "100%", marginBottom: 12 }}
                  value={formData.evidence.physicalEvidenceCollected}
                  onChange={(v) => updateNested("evidence", "physicalEvidenceCollected", v)}
                  disabled={!canEdit}
                  placeholder="Yes / No"
                >
                  {YES_NO.map((o) => (
                    <Option key={String(o.value)} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
                {formData.evidence.physicalEvidenceCollected === true && (
                  <>
                    <label className="field-label-sm">Storage Location</label>
                    <Input
                      value={formData.evidence.physicalEvidenceStorageLocation || ""}
                      onChange={(e) =>
                        updateNested(
                          "evidence",
                          "physicalEvidenceStorageLocation",
                          e.target.value
                        )
                      }
                      disabled={!canEdit}
                      style={{ marginBottom: 12 }}
                    />
                    {renderUpload(SECTION_KEYS.EVIDENCE_PHYSICAL, "Upload Physical Evidence Images")}
                  </>
                )}

                <label className="field-label-sm" style={{ marginTop: 16 }}>
                  Digital Records Retrieved
                </label>
                <div style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {DIGITAL_RECORD_OPTIONS.map((opt) => {
                    const selected = (formData.evidence.digitalRecordsRetrieved || []).includes(opt);
                    return (
                      <Tag.CheckableTag
                        key={opt}
                        checked={selected}
                        onChange={() => canEdit && toggleDigitalRecord(opt)}
                        style={{
                          padding: "4px 12px",
                          fontSize: 14,
                          cursor: canEdit ? "pointer" : "default",
                        }}
                      >
                        {opt}
                      </Tag.CheckableTag>
                    );
                  })}
                </div>
                {(formData.evidence.digitalRecordsRetrieved || []).includes("Other") && (
                  <TextArea
                    rows={2}
                    placeholder="Describe other digital records"
                    value={formData.evidence.digitalRecordsOther || ""}
                    onChange={(e) =>
                      updateNested("evidence", "digitalRecordsOther", e.target.value)
                    }
                    disabled={!canEdit}
                    style={{ marginBottom: 12 }}
                  />
                )}
                {renderUpload(SECTION_KEYS.EVIDENCE_DIGITAL, "Upload Digital Records")}

                <div className="step-nav" style={{ marginTop: 32 }}>
                  <Button onClick={() => setCurrentStep(3)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(5)}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* === STEP 5 — Witnesses + Corrective Actions === */}
            {currentStep === 5 && (
              <div>
                <h2 className="step-title">Step 5: Witnesses & Corrective Actions</h2>

                <h3 style={sectionHeading}>Witness Information</h3>
                <label className="field-label-sm">Witnesses Identified</label>
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={formData.witnesses.identified}
                  onChange={(v) => updateNested("witnesses", "identified", v)}
                  disabled={!canEdit}
                  placeholder="Yes / No"
                >
                  {YES_NO.map((o) => (
                    <Option key={String(o.value)} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>

                {formData.witnesses.identified === true && (
                  <>
                    {(formData.witnesses.list || []).map((w, idx) => (
                      <div className="action-card" key={idx} style={{ marginBottom: 12 }}>
                        <div className="action-card-header">
                          <span style={{ fontSize: 14, fontWeight: 600 }}>
                            Witness {idx + 1}
                          </span>
                          {canEdit && (
                            <button
                              className="btn-delete-icon"
                              onClick={() => removeWitness(idx)}
                            >
                              <DeleteOutlined />
                            </button>
                          )}
                        </div>
                        <label className="field-label-sm">Witness Name</label>
                        <Input
                          value={w.name || ""}
                          onChange={(e) => updateWitness(idx, "name", e.target.value)}
                          disabled={!canEdit}
                          style={{ marginBottom: 12 }}
                        />
                        <label className="field-label-sm">Employer or Company</label>
                        <Input
                          value={w.company || ""}
                          onChange={(e) => updateWitness(idx, "company", e.target.value)}
                          disabled={!canEdit}
                          style={{ marginBottom: 12 }}
                        />
                        <label className="field-label-sm">Factual Statement Summary</label>
                        <TextArea
                          rows={3}
                          value={w.statement || ""}
                          onChange={(e) => updateWitness(idx, "statement", e.target.value)}
                          disabled={!canEdit}
                          style={{ marginBottom: 8 }}
                        />
                        <label className="field-label-sm">
                          <PaperClipOutlined /> Witness Statement Upload
                        </label>
                        {/* Per-witness section key so files reload back to
                            the correct witness card on next open (Brett
                            spec §3 — repeatable section per witness). */}
                        {renderUpload(
                          `${SECTION_KEYS.WITNESS_STATEMENT}_${idx}`,
                          "Upload statement / photo"
                        )}
                      </div>
                    ))}

                    {canEdit && (
                      <Button
                        icon={<PlusOutlined />}
                        onClick={addWitness}
                        style={{ marginBottom: 16 }}
                      >
                        Add Witness
                      </Button>
                    )}
                  </>
                )}

                <h3 style={{ ...sectionHeading, marginTop: 32 }}>Corrective Actions</h3>
                <p className="step-description">
                  Include immediate and factual risk controls only — root cause or
                  systemic preventative actions belong in a separate investigation
                  type.
                </p>

                {(formData.correctiveActions || []).map((action, idx) => {
                  const locked = isCALocked(action);
                  const fieldDisabled = !canEdit || locked;
                  return (
                    <div className="action-card" key={idx}>
                      <div className="action-card-header">
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                          Action {idx + 1}
                        </span>
                        {canEdit && !locked && (
                          <button
                            className="btn-delete-icon"
                            onClick={() => removeCorrectiveAction(idx)}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                      <label className="field-label-sm">Action Description</label>
                      <TextArea
                        rows={3}
                        value={action.action}
                        onChange={(e) => updateCorrectiveAction(idx, "action", e.target.value)}
                        placeholder="Describe the corrective action in detail..."
                        disabled={fieldDisabled}
                        style={{ marginBottom: 16 }}
                      />

                      {(isSafetyAdmin || isSubmitted || isApproved) && (
                        <div className="action-grid">
                          <div>
                            <label className="field-label-sm">Assignee</label>
                            <Select
                              style={{ width: "100%" }}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children || "")
                                  .toString()
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              value={action.assignedTo || undefined}
                              onChange={(val) =>
                                updateCorrectiveAction(idx, "assignedTo", val)
                              }
                              placeholder="Select staff member"
                              disabled={(!isSafetyAdmin || isApproved) || locked}
                            >
                              {staff.map((u) => (
                                <Option key={u.id} value={u.id}>
                                  {u.name || u.full_name || u.email}
                                </Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <label className="field-label-sm">Due Date</label>
                            <Input
                              type="date"
                              min={todayISO()}
                              value={action.dueDate}
                              status={
                                action.dueDate && action.dueDate < todayISO() ? "error" : ""
                              }
                              onChange={(e) =>
                                updateCorrectiveAction(idx, "dueDate", e.target.value)
                              }
                              disabled={(!isSafetyAdmin || isApproved) || locked}
                            />
                          </div>
                        </div>
                      )}

                      {!(isSafetyAdmin || isSubmitted || isApproved) && (
                        <p className="step-description" style={{ fontSize: 12 }}>
                          The Safety Admin will assign this action and set the due
                          date at the approval step.
                        </p>
                      )}
                    </div>
                  );
                })}

                {canEdit && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={addCorrectiveAction}
                    style={{ marginBottom: 24 }}
                  >
                    Add Corrective Action
                  </Button>
                )}

                <div className="step-nav" style={{ marginTop: 32 }}>
                  <Button onClick={() => setCurrentStep(4)}>Back</Button>
                  <div className="step-nav-end">
                    {canEdit && (
                      <Button
                        className="btn-save-draft"
                        onClick={handleSaveDraft}
                        loading={saving || localSaving}
                      >
                        <SaveOutlined /> Save Draft
                      </Button>
                    )}
                    {canSubmit && (
                      <Button
                        type="primary"
                        className="btn-submit"
                        onClick={handleSubmit}
                        loading={submitting || localSubmitting}
                      >
                        <SendOutlined /> Submit Investigation
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Q6 — linked incident attachments. Read-only reference back to the
              original Incident Report so investigators see the original files
              without duplicating storage. */}
          {Array.isArray(investigation?.linked_incident_attachments) &&
            investigation.linked_incident_attachments.length > 0 && (
              <div className="form-card" style={{ marginTop: 16 }}>
                <h3 style={sectionHeading}>Linked Incident Report Attachments</h3>
                <p className="step-description">
                  These files were uploaded with the original Incident Report and
                  are linked here for reference. To add new files, use the per-
                  section uploaders above.
                </p>
                <ul style={{ paddingLeft: 0, listStyle: "none" }}>
                  {investigation.linked_incident_attachments.map((att) => (
                    <li key={att.id} style={{ marginBottom: 6 }}>
                      <PaperClipOutlined style={{ marginRight: 6 }} />
                      <a
                        href={att.full_path || att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {att.display_name ||
                          (att.url ? att.url.split("/").pop() : `Attachment #${att.id}`)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </InvestigationFormWrapper>
      </Box>
    </LayoutWrapper>
  );
};

const sectionHeading = {
  fontSize: 18,
  fontWeight: 600,
  color: "#101828",
  marginTop: 24,
  marginBottom: 12,
};

export default FactualInvestigation;
